/**
 * HomeFeedTabs — the modern tab bar on socialpulse.com's homepage.
 *
 * Replaces the previous UI-only tab bar with a live-data version:
 *   • For You  · blended stories + pulses + urgent causes
 *   • Near You · geo-filtered by useGeo → /api/home/feed?tab=near
 *   • Following · signed-in users see posts from people they follow
 *   • Latest   · newest across the network
 *   • Causes   · active causes + urgent help requests
 *
 * All items land in a single card grid below the tabs.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Home as HomeIcon, MapPin, Users, Clock, Heart, ArrowRight, Loader2, ImageOff,
} from "lucide-react";
import { httpPublic, resolveImage } from "@/lib/api";
import { useGeo } from "@/lib/useGeo";

const TABS = [
  { key: "foryou",    label: "For You",    icon: HomeIcon },
  { key: "near",      label: "Near You",   icon: MapPin },
  { key: "following", label: "Following",  icon: Users },
  { key: "latest",    label: "Latest",     icon: Clock },
  { key: "causes",    label: "Causes",     icon: Heart },
];

function placeSlug(geo) {
  const city = geo?.city;
  if (!city) return "";
  return city.toLowerCase().replace(/\s+/g, "-");
}

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = (Date.now() - then) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}

export default function HomeFeedTabs() {
  const { geo } = useGeo();
  const [tab, setTab] = useState("foryou");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fallback, setFallback] = useState(false);
  const place = useMemo(() => placeSlug(geo), [geo]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { tab, limit: 12 };
    if (tab === "near" && place) params.place = place;
    httpPublic
      .get("/home/feed", { params })
      .then((r) => {
        if (cancelled) return;
        setItems(Array.isArray(r.data?.items) ? r.data.items : []);
        setFallback(Boolean(r.data?.fallback));
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
        setFallback(false);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [tab, place]);

  return (
    <section
      className="max-w-[1360px] mx-auto px-6 lg:px-10 pb-8"
      data-testid="home-feed-tabs"
    >
      {/* Tab bar */}
      <div
        className="mx-auto max-w-3xl bg-white rounded-full shadow-sm p-1 flex items-center gap-1 mb-6 overflow-x-auto"
        role="tablist"
        aria-label="Home feed filters"
        data-testid="home-tab-bar"
      >
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              className={`flex-1 min-w-[92px] inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                active
                  ? "bg-[#1E64C8] text-white shadow-sm"
                  : "text-[#0B1226] hover:bg-[#F0F5FA]"
              }`}
              data-testid={`home-tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Fallback banner */}
      {fallback && !loading && (
        <div className="mb-4 text-xs text-[#50586C] text-center" data-testid="home-feed-fallback-note">
          Showing latest across the network — try another tab or add a location for personalised results.
        </div>
      )}

      {/* Cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-feed-loading">
          {["a", "b", "c", "d", "e", "f"].map((k) => (
            <div key={k} className="animate-pulse rounded-2xl bg-white shadow-sm overflow-hidden">
              <div className="h-36 bg-slate-100" />
              <div className="p-4 space-y-2">
                <div className="h-3 w-20 bg-slate-100 rounded" />
                <div className="h-4 w-3/4 bg-slate-100 rounded" />
                <div className="h-3 w-full bg-slate-100 rounded" />
                <div className="h-3 w-5/6 bg-slate-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="home-feed-empty">
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#F0F5FA] text-[#1E64C8] mb-3">
            <ImageOff className="w-5 h-5" />
          </div>
          <div className="text-sm text-[#50586C]">
            Nothing here yet.{" "}
            <Link to="/publisher/write" className="text-[#1E64C8] font-semibold hover:text-[#164FA0]">
              Be the first to share a story →
            </Link>
          </div>
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-feed-list">
          {items.map((it) => (
            <li key={it.id}>
              <Link
                to={it.link || "#"}
                className="group block h-full rounded-2xl bg-white shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 overflow-hidden"
                data-testid={`home-feed-card-${it.id}`}
              >
                <div
                  className="h-36 w-full relative overflow-hidden"
                  style={{ background: "linear-gradient(135deg, #FDFBF7 0%, #F0F5FA 100%)" }}
                >
                  {it.image ? (
                    <img
                      src={resolveImage(it.image)}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#1E64C8]/60">
                      <HomeIcon className="w-9 h-9" />
                    </div>
                  )}
                  <span
                    className="absolute top-3 left-3 text-[10px] font-semibold text-white px-2 py-1 rounded-full shadow"
                    style={{ background: it.chip_color || "#1E64C8" }}
                  >
                    {it.chip}
                  </span>
                  {it.urgency === "high" || it.urgency === "emergency" ? (
                    <span className="absolute top-3 right-3 text-[10px] font-semibold text-white bg-rose-600 px-2 py-1 rounded-full shadow animate-pulse">
                      {it.urgency === "emergency" ? "URGENT" : "HIGH"}
                    </span>
                  ) : null}
                </div>
                <div className="p-4">
                  <h3 className="text-base font-medium text-[#0B1226] leading-snug line-clamp-2">
                    {it.title}
                  </h3>
                  {it.excerpt && (
                    <p className="mt-1.5 text-sm text-[#50586C] leading-relaxed line-clamp-2">
                      {it.excerpt}
                    </p>
                  )}
                  <div className="mt-3 flex items-center justify-between text-xs text-[#50586C]">
                    <span className="truncate">
                      {it.location || it.author || "SocialPulse"}
                    </span>
                    <span className="shrink-0">{timeAgo(it.created_at)}</span>
                  </div>
                  <span className="mt-2 inline-flex items-center gap-0.5 text-xs font-semibold text-[#1E64C8] group-hover:text-[#164FA0]">
                    Open <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
